import { useTranslation } from "react-i18next";
import { CropImage } from "./utils/ImageTreatment";
import { formattedDate, formattedTime } from "./formatted/FormattedDateAndTime";

const THUMBNAIL_SIZE = 300;

const CompactionThumbnail = ({ container, item, history, label, excluded }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-2 min-w-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <div
        className={`rounded-lg overflow-hidden border border-gray-200 bg-white transition-opacity ${excluded ? "opacity-40" : ""}`}
      >
        {item?.image_url ? (
          <CropImage
            container={container}
            data={item}
            history={history}
            imgDivWidth={THUMBNAIL_SIZE}
            imgDivHeight={THUMBNAIL_SIZE}
            isTooltip={true}
            isMap={false}
            isCharacterization={false}
            displayLegend={false}
            materialsDropdownToDisplay={[]}
            showCheckbox={false}
            displayToBlock={false}
          />
        ) : (
          <div
            className="flex items-center justify-center bg-gray-100 text-gray-400 text-sm"
            style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
          >
            -
          </div>
        )}
      </div>
      <span className="text-xs text-gray-400">
        {formattedDate(item?._time, t)} - {formattedTime(item?._time)}
      </span>
    </div>
  );
};

const CompactionPairCheckbox = ({ checked, disabled, onChange }) => {
  const { t } = useTranslation();
  return (
    <label
      className={`flex items-center gap-2 cursor-pointer select-none ${disabled ? "opacity-50 cursor-wait" : ""}`}
    >
      <input
        type="checkbox"
        className="w-[18px] h-[18px] accent-[#00A7A6] cursor-pointer"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="text-sm font-medium text-[#222222] whitespace-nowrap">
        {checked ? t("Valid compaction") : t("Excluded compaction")}
      </span>
    </label>
  );
};

/**
 * Displays every detected before/after compaction pair as a list (instead
 * of a one-at-a-time carousel), each with a checkbox letting the operator
 * exclude a detection that isn't a real compaction. Excluded pairs stay in
 * the list (greyed out, struck through) rather than disappearing, so the
 * change stays visible and reversible.
 */
export const CompactionPairList = ({
  container,
  history,
  pairs,
  pendingKey,
  errorKey,
  onToggleExcluded,
}) => {
  const { t } = useTranslation();

  if (!pairs || pairs.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-400">
        {t("No compaction recorded yet")}
      </div>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-gray-100">
      {pairs.map((pair) => {
        const key = pair.after?._time;
        const isPending = pendingKey === key;
        const hasError = errorKey === key;

        return (
          <div
            key={key}
            className={`flex flex-col md:flex-row items-center justify-between gap-4 px-6 py-6 transition-opacity ${pair.excluded ? "opacity-60" : ""}`}
          >
            <div className="flex flex-col items-start gap-2 w-full md:w-auto">
              <span
                className={`text-base font-bold text-[#222222] leading-tight ${pair.excluded ? "line-through" : ""}`}
              >
                {`${t("Compaction")} n° ${pair.order}`}
              </span>
              <CompactionPairCheckbox
                checked={!pair.excluded}
                disabled={isPending}
                onChange={(checked) => onToggleExcluded(pair, !checked)}
              />
              {hasError && (
                <span className="text-xs text-red-500">
                  {t("An error occurred")}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6">
              <CompactionThumbnail
                container={container}
                item={pair.before}
                history={history}
                label={t("Before")}
                excluded={pair.excluded}
              />
              <i
                className="fas fa-arrow-right text-gray-300 text-xl"
                aria-hidden
              ></i>
              <CompactionThumbnail
                container={container}
                item={pair.after}
                history={history}
                label={t("After")}
                excluded={pair.excluded}
              />
            </div>

            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold whitespace-nowrap ${
                pair.excluded
                  ? "bg-gray-100 text-gray-400 line-through"
                  : "bg-[#E6F7F5] text-[#00A7A6]"
              }`}
            >
              -{pair.gain}% {t("Filling level gain")}
            </span>
          </div>
        );
      })}
    </div>
  );
};
